// app/page.tsx
'use client';

import { useEffect, useState } from 'react';
import Sidebar from '@/components/Sidebar';
import Header from '@/components/Header';
import StatCard from '@/components/StatCard';
import VerifiedCard from '@/components/VerifiedCard';
import VerificationChart from '@/components/VerificationChart';
import VerificationTrends from '@/components/VerificationTrends';
import DashboardFilters from '@/components/DashboardFilters';
import { Car, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { getVerifiedVehicles, getVerificationStatistics } from './api/verificationApi';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface DashboardStats {
  totalVerified: number;
  totalVehicles: number;
  verificationRate: number;
  dailyAverage: number;
  weeklyAverage: number;
  monthlyAverage: number;
}

interface FilterParams {
  filter: 'all' | 'day' | 'week' | 'month' | 'year';
  date: string;
  municipality: string;
  brand: string;
  year: string;
}

export default function Home() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>({
    totalVerified: 0,
    totalVehicles: 0,
    verificationRate: 0,
    dailyAverage: 0,
    weeklyAverage: 0,
    monthlyAverage: 0,
  });
  const [verifiedVehicles, setVerifiedVehicles] = useState([]);
  const [filters, setFilters] = useState<FilterParams>({
    filter: 'all',
    date: '',
    municipality: '',
    brand: '',
    year: '',
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardData();
  }, [filters]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query parameters
      const params = new URLSearchParams();
      if (filters.filter !== 'all') {
        params.append('filter', filters.filter);
      }
      if (filters.date) params.append('date', filters.date);
      if (filters.municipality) params.append('municipality', filters.municipality);
      if (filters.brand) params.append('brand', filters.brand);
      if (filters.year) params.append('year', filters.year);

      // Fetch statistics
      const statsResponse: any = await getVerificationStatistics(
        filters.filter,
        filters.date || undefined
      );
      
      // Fetch verified vehicles with filters
      const vehiclesResponse: any = await getVerifiedVehicles(
        filters.filter,
        filters.date || undefined
      );

      if (statsResponse.success) {
        const statsData = statsResponse.statistics;
        const summary = statsData.summary || {};

        // Calculate averages from daily data
        const dailyData = statsData.byDay || [];
        const dailyAverage = dailyData.length > 0 
          ? Math.round(dailyData.reduce((acc: number, d: any) => acc + d.count, 0) / dailyData.length)
          : 0;

        const weeklyData = statsData.byWeek || [];
        const weeklyAverage = weeklyData.length > 0
          ? Math.round(weeklyData.reduce((acc: number, d: any) => acc + d.count, 0) / weeklyData.length)
          : 0;

        const monthlyData = statsData.byMonth || [];
        const monthlyAverage = monthlyData.length > 0
          ? Math.round(monthlyData.reduce((acc: number, d: any) => acc + d.count, 0) / monthlyData.length)
          : 0;

        setStats({
          totalVerified: summary.totalVerified || 0,
          totalVehicles: summary.totalVerified || 0,
          verificationRate: summary.totalVerified > 0 ? 100 : 0,
          dailyAverage,
          weeklyAverage,
          monthlyAverage,
        });
      }

      if (vehiclesResponse.success) {
        setVerifiedVehicles(vehiclesResponse.vehicles);
      }

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Impossible de charger les données du tableau de bord');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters: Partial<FilterParams>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleResetFilters = () => {
    setFilters({
      filter: 'all',
      date: '',
      municipality: '',
      brand: '',
      year: '',
    });
  };

  const statCards = [
    {
      title: 'Taxis Enregistrés',
      value: stats.totalVerified.toLocaleString(),
      icon: <Car size={24} />,
      trend: "",
      color: { bg: 'bg-blue-50', text: 'text-primary' },
    },
    // {
    //   title: 'Moyenne Journalière',
    //   value: stats.dailyAverage.toString(),
    //   icon: <Clock size={24} />,
    //   trend: `${stats.weeklyAverage}/semaine`,
    //   color: { bg: 'bg-orange-50', text: 'text-orange-500' },
    // },
    // {
    //   title: 'Taux de Vérification',
    //   value: `${stats.verificationRate}%`,
    //   icon: <CheckCircle size={24} />,
    //   trend: `${stats.monthlyAverage}/mois`,
    //   color: { bg: 'bg-purple-50', text: 'text-purple-600' },
    // },
  ];

  const verifiedCards = [
    {
      title: 'Véhicules Vérifiés',
      subtitle: 'Conformes et certifiés',
      value: `${stats.totalVerified}`,
      tone: 'green' as const,
      count: stats.totalVerified,
    },
    {
      title: 'Vérifications du Jour',
      subtitle: 'Activité récente',
      value: stats.dailyAverage.toString(),
      tone: 'orange' as const,
      count: stats.dailyAverage,
    },
    {
      title: 'Moyenne Hebdomadaire',
      subtitle: `Moyenne de ${stats.weeklyAverage} par semaine`,
      value: `${stats.weeklyAverage}`,
      tone: 'purple' as const,
      count: stats.weeklyAverage,
    },
  ];

  if (loading) {
    return (
      <div className="bg-background-light text-slate-800 font-display h-screen flex">
        <Sidebar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-slate-500">Chargement du tableau de bord...</p>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-background-light text-slate-800 font-display h-screen flex">
        <Sidebar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-md">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto" />
            <h3 className="mt-4 text-lg font-semibold text-slate-800">Erreur de chargement</h3>
            <p className="mt-2 text-slate-500">{error}</p>
            <button
              onClick={() => fetchDashboardData()}
              className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition"
            >
              Réessayer
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="bg-background-light text-slate-800 font-display h-screen flex overflow-hidden">
      <Sidebar />

      <main className="flex-1 flex flex-col h-full relative overflow-hidden bg-background-light">
        <Header title={`Bienvenue, Directeur`} />

        <div className="flex-1 overflow-y-auto p-4 sm:p-8">
          <div className="max-w-7xl mx-auto flex flex-col gap-6">
            {/* Header Section */}
            {/* <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
              <div>
                <p className="text-slate-500 text-sm font-medium">
                  {format(new Date(), 'EEEE d MMMM yyyy', { locale: fr })}
                </p>
                <h2 className="text-2xl font-bold text-slate-800">
                  Bon retour, Directeur
                </h2>
              </div>
            </div> */}

            {/* Filters Section */}
            <DashboardFilters 
              filters={filters}
              onFilterChange={handleFilterChange}
              onReset={handleResetFilters}
            />

            {/* Row 1: KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
              {statCards.map((stat) => (
                <StatCard key={stat.title} {...stat} />
              ))}
            </div>

            {/* Row 2: Verification Progress Cards */}
            {/* <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
              {verifiedCards.map((card) => (
                <VerifiedCard key={card.title} {...card} />
              ))}
            </div> */}

            {/* Main Chart: Verification Trends */}
            <VerificationTrends 
              filter={filters.filter} 
              dateRange={filters.date}
              municipality={filters.municipality}
              brand={filters.brand}
              year={filters.year}
            />

            {/* Detailed Chart: Monthly Registrations */}
            <VerificationChart 
              filter={filters.filter} 
              dateRange={filters.date}
              municipality={filters.municipality}
              brand={filters.brand}
              year={filters.year}
            />
          </div>
        </div>
      </main>
    </div>
  );
}

